import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import CodeIcon from '@mui/icons-material/Code'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import MenuItem from '@mui/material/MenuItem'
import LaunchIcon from '@mui/icons-material/Launch'

import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import { validateField, validateOptionalField, descriptionLimit, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { AdminBookModalStyle } from '../styles'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { deleteCodeNotebook, updateCodeNotebook } from '../../reducers/codenotebooksReducer'
import { openExternal } from '../../utils/safeUrl'

const AdminCodeNotebookModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    // form state
    const [title, setTitle] = useState('')
    const [titleError, setTitleError] = useState(false)
    const [titleHelper, setTitleHelper] = useState('')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [language, setLanguage] = useState('PY')

    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [approved, setApproved] = useState(false)

    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])

    // modal state

    const [open, setOpen] = useState(false)

    // init fields
    useEffect(() => {
        setTitle(props.title)
        setAuthors(props.authors)
        setLink(props.link)
        setDate(props.pub_date)
        setLanguage(props.language)
        setDescription(props.description || '')
        setApproved(props.approved)
        setContentTags((props.content_tags || props.tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))
    }, [props])


    const handleClickOpen = () => {
        setShowTagErrors(false)
        setOpen(true)
    }

    const handleClose = () => {
        setShowTagErrors(false)
        setOpen(false)
    }

    const openResource = () => {
        openExternal(props.link)
        return false;
    }

    const handleEdit = async (event) => {
        event.preventDefault()
        setShowTagErrors(true)
        if (
            validateField(title, setTitleError, setTitleHelper, 200, 'Title') &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateField(authors, setAuthorsError, setAuthorsHelper, 100, 'Authors') &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {
            const data = {
                title: title,
                description: description,
                authors: authors,
                link: link,
                pub_date: date,
                language: language,
                approved: approved,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }

            const ok = await dispatch(updateCodeNotebook(props.id, data, user.staff))
            if (ok) {
                handleClose()
                props.onChanged?.()
            }
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this code notebook? This action cannot be undone.')) {
            handleClose()
            await dispatch(deleteCodeNotebook(props.id, user.staff))
            props.onChanged?.()
        }
    }

    return (
        <div>
            <Button {...AdminBookModalStyle.viewButton.props} sx={AdminBookModalStyle.viewButton.style} onClick={()=>handleClickOpen()}>
                <EditIcon /> &nbsp; Edit
            </Button>
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`admin-codenotebook-modal-${props.id}`}
                open={open}
                fullWidth
                {...AdminBookModalStyle.modal}
            >
                <BootstrapDialogTitle id={`admin-codenotebook-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={AdminBookModalStyle.headingContainer}>
                        <CodeIcon sx={AdminBookModalStyle.icon}/>
                        Code Notebook
                    </Box>
                    <Box sx={AdminBookModalStyle.proposer}>
                        <Typography>
                            {props.proposer? `Created By: ${props.proposer.name} (${props.proposer.email})` : 'Created by unknown user'}
                        </Typography>
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <form onSubmit={handleEdit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={language}
                                    select
                                    label="Programming Language"
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    <MenuItem value="PY">Python</MenuItem>
                                    <MenuItem value="R">R</MenuItem>
                                    <MenuItem value="ML">MATLAB</MenuItem>
                                    <MenuItem value="C">C</MenuItem>
                                    <MenuItem value="CS">C#</MenuItem>
                                    <MenuItem value="CP">C++</MenuItem>
                                    <MenuItem value="JS">JavaScript</MenuItem>
                                    <MenuItem value="JL">Julia</MenuItem>
                                    <MenuItem value="JV">Java</MenuItem>
                                    <MenuItem value="SC">Scala</MenuItem>
                                    <MenuItem value="SQ">SQL</MenuItem>
                                    <MenuItem value="OT">Other</MenuItem>
                                </TextField>
                            </Grid>
                            <CoreResourceFields
                                name={title} setName={setTitle} nameError={titleError} setNameError={setTitleError} nameHelper={titleHelper} setNameHelper={setTitleHelper}
                                authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                                link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                                date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                                validate={validateField}
                                nameLabel="Title"
                                authorsLabel="Authors"
                                dateLabel="Date Updated"
                                showCharCount={true}
                                nameMaxLength={200}
                                authorsMaxLength={100}
                            />
                            <ResourceDescriptionField
                                value={description} setValue={setDescription}
                                error={descriptionError} setError={setDescriptionError}
                                helper={descriptionHelper} setHelper={setDescriptionHelper}
                                limit={descriptionLimit(props.description)}
                            />
                            <ResourceTagsSection
                                contentTags={contentTags} setContentTags={setContentTags}
                                medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                                adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                                experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                                required={true}
                                forceShowRequiredErrors={showTagErrors}
                            />
                            <ResourceCheckboxes
                                inQuiz={inQuiz} setInQuiz={setInQuiz}
                                isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork}
                            />
                            <TCAIREMFieldsSection
                                isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                                isPaid={isPaid} setIsPaid={setIsPaid}
                                tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                                personaTags={personaTags} setPersonaTags={setPersonaTags}
                                forceShowRequiredErrors={showTagErrors}
                            />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={approved}
                                    select
                                    label="Approved"
                                    onChange={(e) => setApproved(e.target.value === 'true')}
                                >
                                    <MenuItem value="true">True</MenuItem>
                                    <MenuItem value="false">False</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={()=>openResource()}>
                        View Notebook&nbsp;<LaunchIcon />
                    </Button>
                    <Button onClick={()=>handleDelete()}>
                        Delete
                    </Button>
                    <Button onClick={(e)=>handleEdit(e)}>
                        Save Changes
                    </Button>
                    <Button onClick={()=>handleClose()}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default AdminCodeNotebookModal
